import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

interface Voice {
  voice_id: string;
  name: string;
  preview_url?: string;
  labels?: {
    accent?: string;
    gender?: string;
    age?: string;
  };
}

interface Avatar {
  avatar_id: string;
  avatar_name: string;
  preview_image_url?: string;
  gender?: string;
}

interface Lesson {
  id: string;
  title: string;
  content: string;
  script?: string;
}

interface Course {
  id: number;
  title: string;
  modules: {
    title: string;
    lessons: Lesson[];
  }[];
}

interface VideoJob {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  video_url?: string;
  error?: string;
}

const VideoGenerator: React.FC = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const [selectedAvatar, setSelectedAvatar] = useState<string>('');
  const [scriptText, setScriptText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState<VideoJob | null>(null);
  const [hasElevenLabs, setHasElevenLabs] = useState(false);
  const [hasHeyGen, setHasHeyGen] = useState(false);
  const [step, setStep] = useState(1);
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCourse();
    checkIntegrations();
  }, [user, courseId, navigate]);

  const fetchCourse = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/courses/${courseId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch course');
      const data = await response.json();
      setCourse(data);
    } catch (error) {
      console.error('Error fetching course:', error);
      showToast('Failed to load course', 'error');
    } finally {
      setLoading(false);
    }
  };

  const checkIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/integrations`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) return;
      const integrations = await response.json();

      const elevenLabs = integrations.find((i: any) => i.integration_type === 'elevenlabs' && i.is_active);
      const heyGen = integrations.find((i: any) => i.integration_type === 'heygen' && i.is_active);

      setHasElevenLabs(!!elevenLabs);
      setHasHeyGen(!!heyGen);

      if (elevenLabs) {
        fetchVoices();
      }
      if (heyGen) {
        fetchAvatars();
      }
    } catch (error) {
      console.error('Error checking integrations:', error);
    }
  };

  const fetchVoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/video/voices`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setVoices(data.voices || []);
      }
    } catch (error) {
      console.error('Error fetching voices:', error);
    }
  };

  const fetchAvatars = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/video/avatars`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvatars(data.avatars || []);
      }
    } catch (error) {
      console.error('Error fetching avatars:', error);
    }
  };

  const handleLessonSelect = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    // Use existing script or generate from content
    setScriptText(lesson.script || lesson.content || '');
    setStep(2);
  };

  const playVoicePreview = (voice: Voice) => {
    if (voice.preview_url) {
      setPreviewingVoice(voice.voice_id);
      const audio = new Audio(voice.preview_url);
      audio.onended = () => setPreviewingVoice(null);
      audio.play().catch(() => setPreviewingVoice(null));
    }
  };

  const handleGenerateVideo = async () => {
    if (!scriptText.trim()) {
      showToast('Please enter a script for the video', 'error');
      return;
    }

    if (!selectedVoice) {
      showToast('Please select a voice', 'error');
      return;
    }

    if (!selectedAvatar) {
      showToast('Please select an avatar', 'error');
      return;
    }

    setGenerating(true);
    setCurrentJob({ id: '', status: 'pending' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/video/generate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            script: scriptText,
            voiceId: selectedVoice,
            avatarId: selectedAvatar,
            lessonTitle: selectedLesson?.title || 'Video',
            courseId: courseId
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate video');
      }

      const data = await response.json();
      setCurrentJob({ id: data.jobId, status: 'processing' });
      showToast('Video generation started! This may take a few minutes.', 'success');

      // Poll for status
      pollJobStatus(data.jobId);
    } catch (error: any) {
      console.error('Error generating video:', error);
      showToast(error.message || 'Failed to generate video', 'error');
      setCurrentJob(null);
      setGenerating(false);
    }
  };

  const pollJobStatus = async (jobId: string) => {
    const token = localStorage.getItem('token');
    const maxAttempts = 60; // 5 minutes max
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/video/status/${jobId}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setCurrentJob(data);

          if (data.status === 'completed') {
            showToast('Video generated successfully!', 'success');
            setGenerating(false);
            setStep(4);
            return;
          } else if (data.status === 'failed') {
            showToast(`Video generation failed: ${data.error || 'Unknown error'}`, 'error');
            setGenerating(false);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000); // Check every 5 seconds
        } else {
          showToast('Video generation timed out. Please check back later.', 'warning');
          setGenerating(false);
        }
      } catch (error) {
        console.error('Error checking job status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000);
        }
      }
    };

    checkStatus();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!hasElevenLabs || !hasHeyGen) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <span className="text-6xl mb-4 block">🎬</span>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Video Generation Setup Required</h1>
            <p className="text-gray-600 mb-6">
              To generate AI videos, you need to configure your ElevenLabs and HeyGen integrations.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className={`p-4 rounded-lg border-2 ${hasElevenLabs ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                <span className="text-3xl block mb-2">🎙️</span>
                <h3 className="font-semibold">ElevenLabs</h3>
                <p className="text-sm text-gray-500">AI Voice Generation</p>
                <p className={`text-sm mt-2 ${hasElevenLabs ? 'text-green-600' : 'text-red-600'}`}>
                  {hasElevenLabs ? '✓ Connected' : '✗ Not configured'}
                </p>
              </div>

              <div className={`p-4 rounded-lg border-2 ${hasHeyGen ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                <span className="text-3xl block mb-2">🎬</span>
                <h3 className="font-semibold">HeyGen</h3>
                <p className="text-sm text-gray-500">AI Avatar Videos</p>
                <p className={`text-sm mt-2 ${hasHeyGen ? 'text-green-600' : 'text-red-600'}`}>
                  {hasHeyGen ? '✓ Connected' : '✗ Not configured'}
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate('/integrations')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Configure Integrations
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/course/${courseId}`)}
            className="text-blue-600 hover:text-blue-700 mb-4 inline-flex items-center"
          >
            ← Back to Course
          </button>
          <h1 className="text-3xl font-bold text-gray-900">AI Video Generator</h1>
          <p className="text-gray-600 mt-2">
            Create professional videos with AI voiceover and avatars for "{course?.title}"
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s}
              </div>
              {s < 4 && (
                <div className={`w-16 h-1 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="text-center mb-6 text-sm text-gray-500">
          {step === 1 && 'Step 1: Select Lesson'}
          {step === 2 && 'Step 2: Choose Voice & Avatar'}
          {step === 3 && 'Step 3: Review & Generate'}
          {step === 4 && 'Step 4: Download Video'}
        </div>

        {/* Step 1: Select Lesson */}
        {step === 1 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Select a Lesson to Create Video</h2>
            <div className="space-y-4">
              {course?.modules.map((module, mIdx) => (
                <div key={mIdx} className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">{module.title}</h3>
                  <div className="space-y-2">
                    {module.lessons.map((lesson, lIdx) => (
                      <button
                        key={lIdx}
                        onClick={() => handleLessonSelect(lesson)}
                        className="w-full text-left p-3 rounded-lg border hover:border-blue-500 hover:bg-blue-50 transition-colors flex justify-between items-center"
                      >
                        <span>{lesson.title}</span>
                        <span className="text-blue-600">Select →</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Custom script option */}
              <div className="border-t pt-4 mt-4">
                <button
                  onClick={() => {
                    setSelectedLesson({ id: 'custom', title: 'Custom Video', content: '' });
                    setScriptText('');
                    setStep(2);
                  }}
                  className="w-full text-left p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <span className="text-lg">📝 Create Custom Video</span>
                  <p className="text-sm text-gray-500 mt-1">Write your own script from scratch</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Voice & Avatar Selection */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Script Editor */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">
                Video Script: {selectedLesson?.title}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Edit the script that will be spoken by your AI avatar. Keep it conversational and engaging!
              </p>
              <textarea
                value={scriptText}
                onChange={(e) => setScriptText(e.target.value)}
                rows={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Enter your video script here..."
              />
              <p className="text-sm text-gray-500 mt-2">
                {scriptText.length} characters (~{Math.ceil(scriptText.length / 150)} min video)
              </p>
            </div>

            {/* Voice Selection */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">🎙️ Select Voice</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {voices.map((voice) => (
                  <button
                    key={voice.voice_id}
                    onClick={() => setSelectedVoice(voice.voice_id)}
                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                      selectedVoice === voice.voice_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-800">{voice.name}</div>
                    {voice.labels && (
                      <div className="text-xs text-gray-500 mt-1">
                        {voice.labels.gender} • {voice.labels.accent}
                      </div>
                    )}
                    {voice.preview_url && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          playVoicePreview(voice);
                        }}
                        className="mt-2 text-blue-600 text-sm hover:underline flex items-center gap-1"
                      >
                        {previewingVoice === voice.voice_id ? '🔊 Playing...' : '▶ Preview'}
                      </button>
                    )}
                  </button>
                ))}
              </div>
              {voices.length === 0 && (
                <p className="text-gray-500 text-center py-8">Loading voices...</p>
              )}
            </div>

            {/* Avatar Selection */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">🎬 Select Avatar</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {avatars.map((avatar) => (
                  <button
                    key={avatar.avatar_id}
                    onClick={() => setSelectedAvatar(avatar.avatar_id)}
                    className={`p-4 rounded-lg border-2 text-center transition-colors ${
                      selectedAvatar === avatar.avatar_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {avatar.preview_image_url ? (
                      <img
                        src={avatar.preview_image_url}
                        alt={avatar.avatar_name}
                        className="w-20 h-20 rounded-full mx-auto object-cover mb-2"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full mx-auto bg-gray-200 flex items-center justify-center mb-2">
                        <span className="text-2xl">👤</span>
                      </div>
                    )}
                    <div className="font-semibold text-gray-800 text-sm">{avatar.avatar_name}</div>
                    {avatar.gender && (
                      <div className="text-xs text-gray-500">{avatar.gender}</div>
                    )}
                  </button>
                ))}
              </div>
              {avatars.length === 0 && (
                <p className="text-gray-500 text-center py-8">Loading avatars...</p>
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!selectedVoice || !selectedAvatar || !scriptText.trim()}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review & Generate */}
        {step === 3 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-6">Review & Generate Video</h2>

            <div className="space-y-4 mb-8">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">Lesson</h3>
                <p className="text-gray-800">{selectedLesson?.title}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">Voice</h3>
                <p className="text-gray-800">{voices.find(v => v.voice_id === selectedVoice)?.name}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">Avatar</h3>
                <p className="text-gray-800">{avatars.find(a => a.avatar_id === selectedAvatar)?.avatar_name}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">Script Preview</h3>
                <p className="text-gray-800 whitespace-pre-wrap">{scriptText.slice(0, 500)}{scriptText.length > 500 ? '...' : ''}</p>
                <p className="text-sm text-gray-500 mt-2">
                  {scriptText.length} characters • ~{Math.ceil(scriptText.length / 150)} minute video
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 text-sm">
                <strong>Note:</strong> Video generation typically takes 2-5 minutes depending on script length.
                You'll be charged based on your ElevenLabs and HeyGen usage.
              </p>
            </div>

            {/* Job Status */}
            {currentJob && (
              <div className={`p-4 rounded-lg mb-6 ${
                currentJob.status === 'completed' ? 'bg-green-50 border border-green-200' :
                currentJob.status === 'failed' ? 'bg-red-50 border border-red-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center gap-3">
                  {currentJob.status === 'processing' && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  )}
                  {currentJob.status === 'completed' && <span className="text-green-600 text-xl">✓</span>}
                  {currentJob.status === 'failed' && <span className="text-red-600 text-xl">✗</span>}
                  <div>
                    <p className="font-semibold">
                      {currentJob.status === 'pending' && 'Starting video generation...'}
                      {currentJob.status === 'processing' && 'Generating your video...'}
                      {currentJob.status === 'completed' && 'Video ready!'}
                      {currentJob.status === 'failed' && 'Generation failed'}
                    </p>
                    {currentJob.error && <p className="text-sm text-red-600">{currentJob.error}</p>}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                disabled={generating}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                ← Back
              </button>
              <button
                onClick={handleGenerateVideo}
                disabled={generating}
                className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  <>🎬 Generate Video</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Download */}
        {step === 4 && currentJob?.video_url && (
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <span className="text-6xl mb-4 block">🎉</span>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Video is Ready!</h2>

            <div className="max-w-2xl mx-auto mb-6">
              <video
                src={currentJob.video_url}
                controls
                className="w-full rounded-lg shadow-lg"
              />
            </div>

            <div className="flex justify-center gap-4">
              <a
                href={currentJob.video_url}
                download={`${selectedLesson?.title || 'video'}.mp4`}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
              >
                ⬇️ Download Video
              </a>
              <button
                onClick={() => {
                  setStep(1);
                  setCurrentJob(null);
                  setSelectedLesson(null);
                  setScriptText('');
                }}
                className="border border-gray-300 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Create Another Video
              </button>
            </div>
          </div>
        )}

        {/* How It Works */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">How AI Video Generation Works</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <span className="text-3xl block mb-2">📝</span>
              <h4 className="font-semibold">1. Script</h4>
              <p className="text-sm text-gray-600">Your lesson content is converted into a video script</p>
            </div>
            <div className="text-center">
              <span className="text-3xl block mb-2">🎙️</span>
              <h4 className="font-semibold">2. Voice</h4>
              <p className="text-sm text-gray-600">ElevenLabs generates realistic voiceover audio</p>
            </div>
            <div className="text-center">
              <span className="text-3xl block mb-2">🎬</span>
              <h4 className="font-semibold">3. Avatar</h4>
              <p className="text-sm text-gray-600">HeyGen creates a talking avatar video synced to the voice</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoGenerator;
